export interface SharePointErrorResponse {
	error?: {
		code?: string;
		message?: string;
		userMessage?: string;
	};
	failed_items?: Array<{
		item_id?: string;
		error?: {
			code?: string;
			message?: string;
			userMessage?: string;
		};
	}>;
	failed_uploads?: Array<{
		file_name?: string;
		error?: {
			code?: string;
			message?: string;
			userMessage?: string;
		};
	}>;
	failed_removals?: Array<{
		file_name?: string;
		error?: {
			code?: string;
			message?: string;
			userMessage?: string;
		};
	}>;
}

const FOLDER_NOT_FOUND_MESSAGE =
	"The SharePoint folder for this report could not be found. Save the report again to recreate it.";
const TEMP_FILE_MISSING_MESSAGE =
	"was never uploaded to SharePoint and its staged copy is no longer available. Remove it from the report and attach the file again.";

const getCodeFromError = (error: any): string | undefined => {
	return (
		error?.error?.code ||
		error?.code ||
		error?.response?.data?.error?.code ||
		error?.data?.error?.code
	);
};

const getUserMessageFromError = (error: any): string | undefined => {
	return (
		error?.error?.userMessage ||
		error?.data?.error?.userMessage ||
		error?.response?.data?.error?.userMessage
	);
};

export const getSharePointErrorMessage = (
	error: any,
	fileName?: string
): string => {
	const code = getCodeFromError(error);
	const serverMessage = getUserMessageFromError(error);

	if (code === "resourceLocked" || code === "notAllowed") {
		const LOCKED_FILE_MESSAGE =
			" is currently open or locked by another user. Close the file in Microsoft 365 and try again.";
		return fileName
			? `${fileName} ${LOCKED_FILE_MESSAGE}`
			: `This file ${LOCKED_FILE_MESSAGE}`;
	}

	if (code === "nameAlreadyExists") {
		return fileName
			? `A file or folder named "${fileName}" already exists. Choose a different name.`
			: "A file or folder with that name already exists.";
	}

	if (serverMessage) {
		return fileName ? `${fileName}: ${serverMessage}` : serverMessage;
	}

	if (code === "folderNotFound") {
		return FOLDER_NOT_FOUND_MESSAGE;
	}

	if (code === "tempFileMissing") {
		return fileName
			? `${fileName} ${TEMP_FILE_MISSING_MESSAGE}`
			: `This file ${TEMP_FILE_MISSING_MESSAGE}`;
	}

	// The report save routes answer validation and setup failures with a plain
	// string in `error` and the cause in `details`.
	const plainServerError =
		typeof error?.error === "string" ? error.error : undefined;
	const serverDetails =
		typeof error?.details === "string" ? error.details : undefined;

	const fallback =
		error?.error?.message ||
		(plainServerError && serverDetails
			? `${plainServerError}: ${serverDetails}`
			: plainServerError) ||
		error?.message ||
		error?.data?.error?.message ||
		error?.response?.data?.error?.message ||
		"An unexpected error occurred.";

	return fileName ? `${fileName}: ${fallback}` : fallback;
};

export const isSharePointLockedError = (error: any): boolean => {
	const code = getCodeFromError(error);
	return code === "resourceLocked" || code === "notAllowed";
};

export const isFolderNotFoundError = (error: any): boolean =>
	getCodeFromError(error) === "folderNotFound";
